import { cardArchiveId, cardDetailId, cardEditId } from "@/assets/googleTrackIds";
import React, { useState } from "react";
import qs from "query-string";
import { Add, ClockCircleOutlined, CuToman } from "@payping/icons";
import {
  Button,
  Card,
  CardList,
  Col,
  Copy,
  Divider,
  Empty,
  Grid,
  Modal,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
  Flex,
} from "@payping/web-components";
import { InvoiceListItemViewModel, InvoiceStatusEnum, invoiceStatus, invoiceV2Apis } from "@payping/api";
import { Link, generatePath, useLocation, useNavigate } from "react-router-dom";
import { getInvoices } from "@payping/redux/actions";
import { submitQueryParams } from "@payping/utils/query-string";
import { DisplayModeEnum, ListCardProps } from "@payping/web-components/components/List/types";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { USER_CLAIMS_LIST, useUserClaim } from "@/utils/useUserClaims";
import generalStyles from "@/assets/scss/Shop/Invoice/scss/index.module.scss";
import HandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import ListFilters from "@/components/DashInvoice/ListFilters";
import { DashboardRoutes } from "@/router";
import { ListBulkActions } from "./listBulkActions";
import { ListBoolQueryParamsType, ListNumberQueryParamsType, ListStringQueryParamsType } from "./types";
import InvoiceDetail from "./Detail";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { useImmerAtom } from "jotai-immer";
import { coreAtom } from "@/core/state/root";

// TODO: CONTENT SHOULD BE FIX

const InvoiceList: React.FC = () => {
  // =========================================
  // Init
  // =========================================
  const [, setCoreState] = useImmerAtom(coreAtom);

  const { lg } = Grid.useBreakpoint();
  const { t } = useAppTranslation();
  const location = useLocation();
  // Because there are different types of filters, we should separate parsing them to avoid conflict between types (like converting "241e43" string to scientific number 2.41e45)
  const parsedSearch = qs.parse(location.search);
  const { searchText, searchDateFrom, searchDateTo }: ListStringQueryParamsType = qs.parse(location.search);
  const {
    status,
    pageNumber = 1,
    pageSize = 10,
  }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });
  const { isArchived }: ListBoolQueryParamsType = qs.parse(location.search, { parseBooleans: true });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const list = useAppSelector((state) => state.invoices.list);
  const listCount = useAppSelector((state) => state.invoices.totalCount);
  const [detailCode, setDetailCode] = useState<string>();
  const [openDrawerDetail, setOpenDrawerDetail] = useState(false);
  const [modal, contextHolder] = Modal.useModal();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const { hasPermission } = useUserClaim([USER_CLAIMS_LIST.invoiceService]);

  const getInvoicePaidByString = (invoice: InvoiceListItemViewModel) => {
    if (invoice.invoicePaidBy === 1) {
      return "پرداخت نقدی";
    }
    if (invoice.invoicePaidBy === 2) {
      return "پرداخت با PCPOS";
    }
    if (invoice.invoicePaidBy === 4) {
      return "پرداخت با پلاگین ها";
    }
    if (invoice.invoicePaidBy === 5) {
      return "پرداخت مستقیم";
    }
    return "";
  };

  const { loading: loadingList, retry } = useMessagedAsyncRetry(
    () => {
      if (hasPermission)
        return dispatch(
          getInvoices({
            category: status,
            isArchived,
            limit: pageSize,
            offset: (pageNumber - 1) * pageSize,
            searchDateFrom,
            searchDateTo,
            searchText,
          }),
        );
      return new Promise((_, reject) => {
        reject();
      });
    },
    [searchText, pageSize, pageNumber, searchDateFrom, searchDateTo, status, isArchived],
    { success: false, useProgress: true },
  );

  const [{ loading: loadingArchive }, archiveInvoiceFn] = useMessagedAsyncFn(
    async (code: string): Promise<void> => {
      if (hasPermission) {
        if (isArchived) await invoiceV2Apis.v2InvoiceDeArchiveByCodePOST(code);
        else await invoiceV2Apis.v2InvoiceArchiveByCodePOST(code);
        setSelectedRowKeys([]);
        await retry();
      }
      return new Promise((_, reject) => {
        reject();
      });
    },
    [retry, isArchived],
    undefined,
    { useProgress: true },
  );

  async function archiveHandler(code?: string): Promise<void> {
    if (code) {
      modal.confirm({
        cancelText: t("cancel"),
        content: isArchived ? t("invoice.unArchive") : t("invoice.archive.confirm"),
        maskClosable: true,
        okText: t("ok"),
        onOk: async () => code && archiveInvoiceFn(code),
      });
    }
  }

  const data: ListCardProps["dataSource"] = list.map((invoice, id) => ({
    key: String(invoice.code),
    actions: [
      <Space key={id} size={"middle"}>
        {/* < جزییات > */}
        {invoice.status === 0 ? (
          <Typography.Link
            onClick={() => {
              navigate(
                { pathname: generatePath(DashboardRoutes.myShop.invoice.edit.url, { code: invoice.code || null }) },
                { state: location.state },
              );
            }}
            key={0}
            data-google-track={cardEditId}
          >
            {t("edit")}
          </Typography.Link>
        ) : null}
        <Typography.Link
          key={1}
          className={"tw-font-medium"}
          onClick={() => {
            setOpenDrawerDetail(true);
            setDetailCode(invoice.code);
          }}
          data-google-track={cardDetailId}
        >
          {t("common.detail")}
        </Typography.Link>

        {/* < آرشیو > */}
        <Typography.Link
          key={2}
          className={"tw-font-medium"}
          data-google-track={cardArchiveId}
          onClick={(): Promise<void> => archiveHandler(invoice.code)}
        >
          {!isArchived ? t("archive") : t("unArchive")}
        </Typography.Link>
      </Space>,
    ],
    content: [
      <Space key={0} size={"middle"}>
        {/* < قیمت > */}
        <Typography.Text className={"tw-font-medium"}>
          <Flex align="center">
            <Typography.Text> {formatLocalizedNumber({ value: invoice.total ?? 0 })}</Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        </Typography.Text>

        {/* < تگ > */}
        {invoice.status !== undefined ? (
          <Tag color={invoice.invoicePaidBy === 1 ? "default" : invoiceStatus[invoice.status].color}>
            {invoiceStatus[invoice.status].name}{" "}
            {invoice.status === InvoiceStatusEnum.PAID
              ? invoice.invoicePaidBy !== 0 && `( ${getInvoicePaidByString(invoice)} )`
              : ""}{" "}
            {invoice.isCorrection ? t("correction") : ""}
          </Tag>
        ) : null}
      </Space>,

      /* < نام مشتری > */
      <Typography.Text className={"tw-font-medium"} key={1}>
        {invoice.customer?.fullName || `${invoice.customer?.firstName ?? ""} ${invoice.customer?.lastName ?? ""}`}
      </Typography.Text>,
    ],
    title: (
      <Space>
        {/* < تایتل > */}
        <Typography.Text className={"tw-font-medium"} italic={invoice.status === InvoiceStatusEnum.DRAFT}>
          {invoice.title}
        </Typography.Text>

        {/* < آیکون ساعت > */}
        {invoice.isSchedule ? (
          <Tooltip title={t("invoice.detail.hasSchedule")}>
            <ClockCircleOutlined width={15} className={"color--gray500"} />
          </Tooltip>
        ) : null}
      </Space>
    ),
    descriptions: (
      <Typography.Text type={"secondary"} className={"tw-pt-1"}>
        <Space size={[0, 0]}>
          {/* < تاریخ > */}
          {toJalali({ date: invoice.sendDate, toFormat: dateFormats.ui.jalaliDate }).string}

          {/* < | > */}
          <Divider type={"vertical"} />

          {/* < کپی کد > */}

          <Copy text={invoice.code || ""}>
            <Typography.Text
              className="!tw-dir-ltr tw-inline-block"
              type={"secondary"}
            >{`${(invoice.code ?? "")?.slice(0, 4)} ...`}</Typography.Text>
          </Copy>
        </Space>
      </Typography.Text>
    ),
  }));

  const loading = loadingList || loadingArchive;

  // =========================================
  // Render
  // =========================================
  return (
    <HandlingCrispInMobile>
      <>
        {loading || data?.length > 0 || location.search ? (
          <Space direction={"vertical"} size={"middle"} className={"tw-w-full"}>
            <ListFilters retry={retry} loading={loading} setSelectedRowKeys={setSelectedRowKeys} />
            <CardList
              loading={!data.length ? loading : false}
              className={generalStyles.tableWrapper}
              dataSource={data}
              grid={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1, column: 1 }}
              pagination={{
                onChange: (page, newSize): void => {
                  setSelectedRowKeys([]);
                  const newValues = {
                    ...parsedSearch,
                    pageNumber: page,
                    pageSize: newSize,
                  };
                  submitQueryParams({
                    navigate,
                    newValues,
                    parsedSearch,
                    pathname: location.pathname,
                    retry,
                  });
                },
                onShowSizeChange: (_currentPage, newSize): void => {
                  const newValues = {
                    ...parsedSearch,
                    pageNumber: 1,
                    pageSize: newSize,
                  };
                  submitQueryParams({
                    navigate,
                    newValues,
                    parsedSearch,
                    pathname: location.pathname,
                    retry,
                  });
                },
                pageNumber,
                pageSize,
                total: listCount,
              }}
              itemSelection={{
                onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys.map((r) => String(r))),
                selectedItemKeys: selectedRowKeys,
              }}
              mode={lg ? DisplayModeEnum.List : DisplayModeEnum.Card}
            />
          </Space>
        ) : (
          <Empty
            description={
              <Space direction={"vertical"} size={"small"} className={"tw-w-full"}>
                <Typography.Text strong>{t("invoice.emptyList.title")}</Typography.Text>
                <Typography.Text>{t("invoice.emptyList.content")}</Typography.Text>
                <Button
                  icon={<Add width={14} height={14} />}
                  key={0}
                  type={"primary"}
                  onClick={() => {
                    if (hasPermission) {
                      navigate({ pathname: DashboardRoutes.myShop.invoice.create.url }, { state: location.state });
                    } else {
                      setCoreState((draft) => {
                        draft.upgradeUserPanel.toPro.service = "invoice";
                        draft.upgradeUserPanel.toPro.showModal = true;
                      });
                    }
                  }}
                >
                  {t("add.button", { subject: t("invoice.title") })}
                </Button>
              </Space>
            }
          >
            <Row gutter={[8, 8]} className={"tw-w-full"}>
              <Col xs={24} md={12}>
                <Card.Ant className="tw-text-start">
                  <Space direction={"vertical"} size={"small"} className={"tw-w-full"}>
                    <Typography.Text type={"secondary"} className={"tw-text-xs"}>
                      {t("invoice.emptyList.cards.invoice.upperTitle")}
                    </Typography.Text>
                    <Typography.Text strong> {t("invoice.emptyList.cards.invoice.title")}</Typography.Text>
                    <Typography.Text type={"secondary"}>{t("invoice.emptyList.cards.invoice.content")}</Typography.Text>
                    <Space>
                      <Link to={DashboardRoutes.connections.developers.root.url}>
                        <Button type={"link"} className={"!tw-p-0"}>
                          {t("common.receiveToken")}
                        </Button>
                      </Link>

                      <Link to={DashboardRoutes.connections.developers.root.url}>
                        <Button type={"link"} className={"!tw-p-0"}>
                          {t("common.guideToTechnicalDocumentation")}
                        </Button>
                      </Link>
                    </Space>
                  </Space>
                </Card.Ant>
              </Col>
              <Col xs={24} md={12}>
                <Card.Ant className="tw-text-start">
                  <Space direction={"vertical"} size={"small"} className={"tw-w-full"}>
                    <Typography.Text type={"secondary"} className={"tw-text-xs"}>
                      {t("invoice.emptyList.cards.plugins.upperTitle")}
                    </Typography.Text>
                    <Typography.Text strong> {t("invoice.emptyList.cards.plugins.title")}</Typography.Text>
                    <Typography.Text type={"secondary"}>
                      {t("invoice.emptyList.cards.plugins.content")}{" "}
                    </Typography.Text>
                    <Link to={DashboardRoutes.connections.developers.root.url}>
                      <Button type={"link"} className={"!tw-p-0"}>
                        {t("common.readMore")}
                      </Button>
                    </Link>
                  </Space>
                </Card.Ant>
              </Col>
            </Row>
          </Empty>
        )}
      </>
      <ListBulkActions
        retry={retry}
        isArchived={isArchived}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        data={data}
      />
      {contextHolder}
      {detailCode ? (
        <InvoiceDetail
          code={detailCode}
          retryList={retry}
          setCode={setDetailCode}
          openDrawerDetail={openDrawerDetail}
          setOpenDrawerDetail={setOpenDrawerDetail}
        />
      ) : null}
    </HandlingCrispInMobile>
  );
};

export default InvoiceList;
